import React, { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import ReusableTable from "../dashboard/ReusableTable";
import ReusableForm from "../common/ReusableForm";
import SubTaskDialog from "./SubTaskDialog";
import { getRequest, postRequest } from "../services/apiService"; // Removed putRequest and deleteRequest
import ReusableDeleteDialog from "../common/ReusableDeleteDialog";

const ActionCell = ({ rowData, onEdit, onDelete, onSubTask }) => {
  const op = useRef(null);
  const menuItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        op.current.hide();
        onEdit(rowData);
      }
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        op.current.hide();
        onDelete(rowData);
      }
    },
    {
      label: "Sub Task",
      icon: "pi pi-plus",
      command: () => {
        op.current.hide();
        onSubTask(rowData);
      }
    }
  ];

  return (
    <>
      <Button
        icon="pi pi-ellipsis-h"
        className="p-button-rounded p-button-text"
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel ref={op} className="p-0 shadow-lg">
        <Menu model={menuItems} className="border-none" />
      </OverlayPanel>
    </>
  );
};

export default function TaskManagementApp() {
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({
    id: null,
    name: "",
    milestone: "",
    equipment_as_task: "",
    contribution: "0",
  });
  const [filters, setFilters] = useState({
    milestone: "",
    status: ""
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubTaskDialogOpen, setIsSubTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [milestoneOptions, setMilestoneOptions] = useState([
    { label: "Select Milestone", value: "" }
  ]);
  const [statusOptions, setStatusOptions] = useState([
    { label: "Select Status", value: "" },
    { label: "Active", value: "1" },
    { label: "Inactive", value: "0" }
  ]);
  
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirm Deletion",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const [deleteFormData, setDeleteFormData] = useState({
    confirmation: ""
  });

  const taskFormFields = [
    {
      name: "name",
      label: "Task Name",
      type: "text",
      required: true,
      placeholder: "Enter task name"
    },
    {
      name: "milestone",
      label: "Milestone",
      type: "select",
      required: true,
      options: milestoneOptions,
      placeholder: "Select milestone"
    },
    {
      name: "equipment_as_task",
      label: "Equipment",
      type: "select",
      required: true,
      options: [
        { label: "Select Equipment", value: "" },
        ...(equipmentOptions || []).map(equip => ({
          label: equip.name,
          value: String(equip.id)
        }))
      ],
      placeholder: "Select Equipment"
    },
    {
      name: "contribution",
      label: "Contribution (%)",
      type: "text",
      required: true,
      placeholder: "Enter contribution percentage"
    }
  ];

  const normalizeResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (data.results && Array.isArray(data.results.data)) return data.results.data;
    if (data.data && Array.isArray(data.data)) return data.data;
    return [];
  };

  // Fetch tasks for the current page and search
  const fetchTasks = async (page = currentPage, searchValue = search) => {
    setLoading(true);
    try {
      const response = await getRequest(`/master/tasks/?search=${encodeURIComponent(searchValue)}&page=${page}`);
      const tasksData = normalizeResponse(response).map((task) => ({
        ...task,
        milestone: task.milestone ? String(task.milestone.id) : "",
        equipment_as_task: task.equipment_as_task ? String(task.equipment_as_task.id) : "",
        parent_task: task.parent_task ? String(task.parent_task.id) : "",
        milestone_name: task.milestone ? task.milestone.title : "",
        equipment_name: task.equipment_as_task ? task.equipment_as_task.name : "",
        parent_task_name: task.parent_task ? task.parent_task.name : "",
        milestone_obj: task.milestone,
        equipment_obj: task.equipment_as_task,
        parent_task_obj: task.parent_task
      }));
      setTasks(tasksData);
      setTotalRecords(response.count || 0);
    } catch (e) {
      // handle error
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect for table search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchTasks(currentPage, search);
    }, 700);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [search, currentPage]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const fetchEquipment = async () => {
    try {
      const { data } = await getRequest("/master/equipment/");
      setEquipmentOptions(data);
    } catch (err) {
      console.error("Error fetching equipment:", err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch equipment list",
        life: 3000
      });
    }
  };

  const fetchMilestones = async () => {
    try {
      const response = await getRequest("/master/milestones/");
      if (response && response.status === "success" && response.data) {
        const options = response.data.map(milestone => ({
          label: milestone.title,
          value: String(milestone.id)
        }));
        setMilestoneOptions([
          { label: "Select Milestone", value: "" },
          ...options
        ]);
      }
    } catch (error) {
      console.error("Error fetching milestones:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch milestones",
        life: 3000
      });
    }
  };

  useEffect(() => {
    fetchEquipment();
    fetchMilestones();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const openEditModal = (task) => {
    setNewTask({
      id: task.id,
      name: task.name || "",
      milestone: task.milestone || "",
      equipment_as_task: task.equipment_as_task || "",
      contribution: task.contribution != null ? String(task.contribution) : "0",
    });
    
    setIsEditModalOpen(true);
  };

  const openDeleteDialog = (task) => {
    setSelectedTask(task);
    setDeleteFormData({ confirmation: "" });
    setIsDeleteDialogOpen(true);
  };

  const openSubTaskModal = (task) => {
    setSelectedTask(task);
    setIsSubTaskDialogOpen(true);
  };

  const openAddDialog = () => {
    setNewTask({
      id: null,
      name: "",
      milestone: "",
      equipment_as_task: "",
      contribution: "0",
    });
    setIsEditModalOpen(true);
  };

  const showNotification = (message, type) => {
    toast.current.show({
      severity: type,
      summary: type === 'success' ? 'Success' : type === 'warning' ? 'Warning' : 'Error',
      detail: message,
      life: 3000
    });
  };

  const handleSubmit = async () => {
    if (!newTask.name) {
      showNotification("Task name is required", "error");
      return;
    }
    try {
      const payload = {
        ...newTask,
        contribution: parseFloat(newTask.contribution || 0),
        equipment_as_task: newTask.equipment_as_task ? parseInt(newTask.equipment_as_task) : null,
        milestone: newTask.milestone ? parseInt(newTask.milestone) : null,
        status_db: 1 // Default status
      };
      
      // Use POST for both create and update
      if (newTask.id) {
        // UPDATE: Send as POST with ID in payload
        await postRequest("/master/tasks/", payload);
        showNotification("Task updated successfully", "success");
        await fetchTasks();
      } else {
        // CREATE: Send as POST without ID
        const { id, ...postPayload } = payload;
        await postRequest("/master/tasks/", postPayload);
        showNotification("Task added successfully", "success");
        await fetchTasks();
      }
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Error saving task:", error);
      showNotification("Failed to save task", "error");
    }
  };

  const confirmDelete = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000
      });
      return;
    }
    try {
      // Soft delete using POST with status_db=3
      await postRequest(`/master/tasks/`, {
        id: selectedTask.id,
        status_db: 3
      });
      showNotification('Task deleted successfully', 'warning');
      setIsDeleteDialogOpen(false);
      setSelectedTask(null);
      setDeleteFormData({ confirmation: "" });
      await fetchTasks();
    } catch (error) {
      console.error("Error deleting task:", error);
      showNotification('Failed to delete task', 'error');
    }
  };

  const filteredTasks = tasks.filter((task) => {
    return (
      (filters.milestone === "" || task.milestone === filters.milestone) &&
      (filters.status === "" || task.status_db === Number(filters.status))
    );
  });

  const columns = [
    
    { 
      field: "name", 
      header: "Task Name", 
      sortable: true 
    },
    { 
      field: "milestone_name", 
      header: "Milestone", 
      sortable: true 
    },
    { 
      field: "equipment_name", 
      header: "Equipment", 
      sortable: true 
    },
    { 
      field: "contribution", 
      header: "Contribution (%)", 
      sortable: true,
      body: (rowData) => `${rowData.contribution}%`
    },
    {
      field: "actions",
      header: "Action",
      body: (rowData) => (
        <ActionCell
          rowData={rowData}
          onEdit={openEditModal}
          onDelete={openDeleteDialog}
          onSubTask={openSubTaskModal}
        />
      ),
      sortable: false,
      notFilterable: true
    }
  ];

  const filterControls = (
    <div className="flex flex-wrap gap-3 mb-4">
      <div className="flex-1 min-w-[200px]">
        <label htmlFor="milestoneFilter" className="block text-sm font-medium text-gray-700 mb-1">
          Milestone
        </label>
        <select
          id="milestoneFilter"
          name="milestone"
          value={filters.milestone}
          onChange={handleFilterChange}
          className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Milestones</option>
          {milestoneOptions.slice(1).map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      <div className="flex-1 min-w-[200px]">
        <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
          Status
        </label>
        <select
          id="statusFilter"
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
          className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Statuses</option>
          {statusOptions.slice(1).map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <Toast ref={toast} position="top-right" />
      <div className="max-w-full mx-auto">
        
        <ReusableTable
          title="Task Manager"
          data={filteredTasks}
          columnFields={columns}
          rows={rowsPerPage}
          colorScheme={{
            headerBgColor: '#1e3a8a',
            headerTextColor: '#ffffff',
            tableBgColor: '#ffffff',
            tableTextColor: '#334155',
            buttonBgColor: '#3b82f6',
            buttonTextColor: '#ffffff',
            deleteButtonColor: '#ef4444',
            addButtonColor: '#10b981',
            uploadButtonColor: '#f59e0b',
            exportButtonColor: '#6366f1'
          }}
          setAddNewDialogVisible={openAddDialog}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          first={(currentPage - 1) * rowsPerPage}
          loading={loading}
          searchValue={search}
          onSearchChange={handleTableSearch}
          paginator
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          rowsPerPageOptions={[5, 10, 25, 50]}
        />

        {/* Add/Edit Task Modal */}
        {isEditModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-lg">
              <ReusableForm
                formData={newTask} // newTask is used for both add and edit data
                setFormData={setNewTask}
                fields={taskFormFields}
                onSubmit={handleSubmit}
                onHide={() => setIsEditModalOpen(false)}
                loading={loading} // Assuming 'loading' state is relevant for task submission
                headerText={newTask.id ? "Edit Task" : "Add New Task"}
              />
            </div>
          </div>
        )}

        <ReusableDeleteDialog
          visible={isDeleteDialogOpen}
          onHide={() => setIsDeleteDialogOpen(false)}
          onDelete={confirmDelete}
          fields={deleteFormFields}
          formData={deleteFormData}
          setFormData={setDeleteFormData}
          loading={loading}
          title="Delete Task"
          description={`Are you sure you want to delete task "${selectedTask?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
          deleteLabel="Delete"
          style={{ zIndex: 2000 }}
        />

        {/* SubTask Dialog Modal Overlay */}
        {isSubTaskDialogOpen && (
          <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-transparent w-full max-w-2xl animate-fade-in">
              <SubTaskDialog
                visible={isSubTaskDialogOpen}
                onHide={() => {
                  setIsSubTaskDialogOpen(false);
                  fetchTasks(currentPage);
                }}
                taskData={selectedTask}
                fetchTasks={() => fetchTasks(currentPage)}
                toast={toast}
                isModalOverlay={true}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}